import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi } from "@/services/apiClient";
import { MAX_RANGE_DAYS, daysAgoISODate, todayISODate } from "@/stores/filterStore";
import { rebucketToolSeries, cumulativeToolSeries, rebucketModelCountSeries, cumulativeModelCountSeries, formatBucketLabel } from "@/lib/timeseries";
import { SUPER_GROUP_ORDER, fmt, toolLabel, prettyModelId } from "@/lib/format";
import KpiCard, { KpiRow } from "@/components/KpiCard";
import ModelUsageView from "@/components/ModelUsageView";
import { ALL_ENGINE_OPTIONS, FILTER_GROUPS, engineKey } from "@/lib/engineFilterGroups";
import { buildDisplayItems, itemValue, itemTotal, grandTotal } from "@/lib/toolUsageDisplay";
import EngineGroupFilterDropdown from "@/components/EngineGroupFilterDropdown";
import EngineUsageTrendChart from "@/components/EngineUsageTrendChart";
import EngineUsageTrendTable from "@/components/EngineUsageTrendTable";
import SectionRangePicker from "@/components/SectionRangePicker";
import ExportButton from "@/components/ExportButton";
import CopyButton from "@/components/CopyButton";
import ToggleGroup from "@/components/ToggleGroup";
import InfoTooltip from "@/components/InfoTooltip";
import Panel from "./Panel";
import Skeleton from "./Skeleton";

const GRANULARITY_OPTIONS = [
  { id: "day",   label: "일별" },
  { id: "week",  label: "주별" },
  { id: "month", label: "월별" },
];

// "무엇을 했나(도구)" vs "어떤 엔진이 일했나(모델)" — 같은 사용량을 절단면만 바꿔 본다
// (2026-07-18, 탭을 "사용량"으로 넓히면서 추가). 목적이 같아서 새 탭이 아니라 뷰 토글.
const VIEW_OPTIONS = [
  { id: "tool",  label: "도구별" },
  { id: "model", label: "모델별" },
];

// "구간별/누적" 토글 라벨 — 전체 사용자 탭의 가입자 추이도 동일 라벨로 통일(2026-07-17,
// 예전엔 그 쪽만 "신규/누적"이라 같은 개념이 탭마다 다르게 불렸음).
const MODE_OPTIONS = [
  { id: "period",     label: "구간별" },
  { id: "cumulative", label: "누적" },
];

const UNIT_OPTIONS = [
  { id: "messages", label: "메시지 수" },
  { id: "sessions", label: "세션 수" },
];

// Population Scope 토글 (2026-08-25) — 사용량은 기본 '고객'(실제 고객 행동 분석), 필요하면
// '전체'(내부 QA·Admin 포함 시스템 전체 사용량)로 전환. frontend 재필터 아니라 API scope 파라미터로
// 서버에서 population 을 바꿔 조회한다. 'customer' 는 유료 고객이 아니라 실제 고객(무료·프로모션 포함).
const SCOPE_OPTIONS = [
  { id: "customer", label: "고객" },
  { id: "all", label: "전체" },
];

function defaultRange() {
  return { from: daysAgoISODate(MAX_RANGE_DAYS - 1), to: todayISODate() };
}

// "요청 수"는 메시지 수의 항상 1/2(=고정 배수)라 별도 정보가 없어서 옵션에서 제외(2026-07-16) —
// 메시지 수/세션 수만 남김(세션 수는 배수 관계가 아니라 실제로 다른 정보를 담고 있음).

export default function ToolUsageSection() {
  const [view, setView] = useState("tool"); // "tool" | "model"
  const [granularity, setGranularity] = useState("week");
  const [mode, setMode] = useState("period"); // "period" | "cumulative"
  const [unit, setUnit] = useState("messages"); // "messages" | "sessions"
  const [range, setRange] = useState(defaultRange);
  // 기본값 전체 선택 — "우리가 가진 엔진들"을 한눈에 보여달라는 요청 반영.
  const [selected, setSelected] = useState(ALL_ENGINE_OPTIONS.map(({ tool, engine }) => ({ tool, engine })));
  // 기본값 전부 접힘(그룹 단위 합계) — 라인/컬럼이 10개를 안 넘어서 한눈에 들어온다.
  // 특정 그룹만 화살표로 펼치면 그 그룹의 엔진별 디테일이 드러난다(2026-07-16, 스파게티 차트 개선).
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Population Scope — URL(?scope=)에 반영해 새로고침·딥링크에서 유지. 유효하지 않거나
  // 미지정이면 'customer'(사용량 화면 기본은 고객). 'all' 이면 내부 포함 전체.
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeParam = searchParams.get("scope");
  const scope = SCOPE_OPTIONS.some((o) => o.id === scopeParam) ? scopeParam : "customer";
  const setScope = (id) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("scope", id);
    return next;
  }, { replace: true });

  const { data: toolTs, loading, error } = useAnalyticsQuery(
    (p, s) => analyticsApi.timeseries({ ...p, granularity: "day", byTool: 1, byModelCost: 1, scope }, s),
    [range.from, range.to, scope],
    range
  );
  const bucketed = rebucketToolSeries(toolTs?.toolSeries, granularity, range);
  const series = (mode === "cumulative" ? cumulativeToolSeries(bucketed) : bucketed)
    .map((r) => ({ ...r, bucketLabel: formatBucketLabel(r.bucket, granularity) }));
  // 모델별 뷰에도 도구 필터 적용(2026-07-18) — 백엔드가 버킷마다 도구·엔진×모델 교차
  // 카운트(byToolEngineModelCount)를 내려주므로, 선택된 조합만 골라 byModelCount 를
  // 재구성한다. 전체 선택(기본값)일 땐 원본 그대로 — 알려진 조합 밖(도구 계측 전 기록
  // 등)의 사용량까지 포함된 "전체" 숫자를 유지하기 위해서다(필터를 좁힐 때만 교차
  // 카운트로 전환).
  const allEnginesSelected = selected.length === ALL_ENGINE_OPTIONS.length;
  const selKeys = new Set(selected.map((s) => `${s.tool}|${s.engine}`));
  const filteredModelCostSeries = allEnginesSelected
    ? toolTs?.modelCostSeries
    : (toolTs?.modelCostSeries || []).map((b) => {
        const byModelCount = {};
        for (const [te, models] of Object.entries(b.byToolEngineModelCount || {})) {
          if (!selKeys.has(te)) continue;
          for (const [m, c] of Object.entries(models)) byModelCount[m] = (byModelCount[m] || 0) + c;
        }
        return { ...b, byModelCount };
      });
  const modelBucketed = rebucketModelCountSeries(filteredModelCostSeries, granularity, range);
  const modelSeries = (mode === "cumulative" ? cumulativeModelCountSeries(modelBucketed) : modelBucketed)
    .map((r) => ({ ...r, bucketLabel: formatBucketLabel(r.bucket, granularity) }));

  // 탭 상단 KPI — 필터/누적 모드와 무관하게 "이 기간 전체" 기준으로 계산한다(필터를 바꿀
  // 때마다 요약 숫자가 흔들리면 기준점 역할을 못 함). 세션은 같은 대화가 여러 날/도구에
  // 걸치면 중복되므로 id 합집합(union) 크기로 계산.
  const toolTotals = {};
  const kpiSessionIds = new Set();
  for (const r of bucketed) {
    for (const [tool, c] of Object.entries(r.byTool || {})) toolTotals[tool] = (toolTotals[tool] || 0) + c;
    for (const ids of Object.values(r.byToolEngineSessionIds || {})) ids.forEach((id) => kpiSessionIds.add(id));
  }
  const totalRequests = Object.values(toolTotals).reduce((s, c) => s + c, 0);
  // "분류 미설정"(chat/default — 도구 계측 전 기록·tool 미지정 채팅)은 도구가 아니라서
  // 최다 사용 도구 후보에서 제외(2026-07-18) — 미분류가 1위로 뜨면 이 카드가 "우리 도구 중
  // 뭐가 제일 쓰이나"라는 질문에 답을 못 한다. 제외한 양은 sublabel 에 정직하게 표기.
  const UNCLASSIFIED_TOOLS = new Set(["chat", "default"]);
  const unclassifiedCount = Object.entries(toolTotals)
    .filter(([t]) => UNCLASSIFIED_TOOLS.has(t))
    .reduce((s, [, c]) => s + c, 0);
  const topTool = Object.entries(toolTotals)
    .filter(([t]) => !UNCLASSIFIED_TOOLS.has(t))
    .sort((a, b) => b[1] - a[1])[0];
  // 모델별 기간 합계 — 3번째 KPI(최다 호출 모델)용. KPI 는 toolTotals 와 같은 원칙으로
  // 필터·누적 모드와 무관하게 "이 기간 전체" 기준(원본 modelCostSeries) — 필터를 바꿀
  // 때마다 요약 숫자가 흔들리면 기준점 역할을 못 한다.
  const modelTotals = {};
  for (const b of toolTs?.modelCostSeries || []) {
    for (const [m, c] of Object.entries(b.byModelCount || {})) modelTotals[m] = (modelTotals[m] || 0) + c;
  }
  // 복사/CSV·차트가 쓰는 필터 반영 합계 — 재집계본 행은 호출 수를 `byModel` 키에 담는다
  // (백엔드 원본의 byModelCount 와 이름이 달라서 한 번 틀렸던 지점, 2026-07-18).
  const filteredModelTotals = {};
  for (const r of modelBucketed) {
    for (const [m, c] of Object.entries(r.byModel || {})) filteredModelTotals[m] = (filteredModelTotals[m] || 0) + c;
  }
  const topModel = Object.entries(modelTotals).sort((a, b) => b[1] - a[1])[0];
  const items = buildDisplayItems(selected, expandedGroups, SUPER_GROUP_ORDER);
  const csvRows = [
    [...["기간"], ...items.map((it) => it.label), "합계"],
    ...series.map((r) => [
      r.bucketLabel || r.bucket,
      ...items.map((it) => itemValue(r, it, unit)),
      unit === "sessions" ? grandTotal([r], items, unit) : items.reduce((s, it) => s + itemValue(r, it, unit), 0),
    ]),
    ["합계", ...items.map((it) => itemTotal(series, it, unit)), grandTotal(series, items, unit)],
  ];
  // 모델별 뷰 복사/CSV — 도구별과 같은 구조(기간=행, 항목=열, 합계 행), 호출 수 기준.
  const modelIds = Object.keys(filteredModelTotals).sort((a, b) => filteredModelTotals[b] - filteredModelTotals[a]);
  const modelCsvRows = [
    ["기간", ...modelIds.map((m) => prettyModelId(m)), "합계"],
    ...modelSeries.map((r) => [
      r.bucketLabel || r.bucket,
      ...modelIds.map((m) => (r.byModel || {})[m] || 0),
      modelIds.reduce((s, m) => s + ((r.byModel || {})[m] || 0), 0),
    ]),
    ["합계", ...modelIds.map((m) => filteredModelTotals[m]), Object.values(filteredModelTotals).reduce((s, c) => s + c, 0)],
  ];
  const exportRows = view === "tool" ? csvRows : modelCsvRows;

  function toggleEngine(opt) {
    setSelected((prev) => {
      const key = engineKey(opt);
      const exists = prev.some((p) => engineKey(p) === key);
      return exists ? prev.filter((p) => engineKey(p) !== key) : [...prev, opt];
    });
  }

  function toggleGroup(superGroup, engines) {
    setSelected((prev) => {
      const groupKeys = new Set(engines.map(engineKey));
      const allSelected = engines.every((e) => prev.some((p) => engineKey(p) === engineKey(e)));
      if (allSelected) return prev.filter((p) => !groupKeys.has(engineKey(p)));
      const withoutGroup = prev.filter((p) => !groupKeys.has(engineKey(p)));
      return [...withoutGroup, ...engines];
    });
  }

  function toggleExpand(superGroup) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(superGroup)) next.delete(superGroup);
      else next.add(superGroup);
      return next;
    });
  }

  return (
    <Panel
      title={
        <span className="inline-flex items-center gap-1.5">
          사용량
          <InfoTooltip
            intro="같은 사용량을 두 절단면으로 봅니다."
            items={[
              { term: "도구별 / 모델별", desc: "무엇을 했나 / 어떤 AI 엔진이 일했나" },
              { term: "그룹 펼치기", desc: "도구 필터의 화살표(▸)로 엔진별 세부 흐름까지" },
              { term: "메시지 수", desc: "요청 수 × 2 (사용자+AI 응답)" },
              { term: "세션 수", desc: "여러 도구·기간에 걸쳐도 중복 없는 고유 대화 수" },
              { term: "모델별 추이", desc: "호출 수 기준" },
            ]}
          />
        </span>
      }
      action={
        /* 뷰를 전환해도 컨트롤들이 나타났다 사라지며 자리가 밀리지 않게(2026-07-18
           "위치는 그대로, 일관성" 피드백) 전부 상시 렌더링 — 지금 뷰에서 의미 없는
           것(도구 필터·단위 토글)만 비활성으로 흐려진 채 자리를 지킨다. */
        <div className="flex flex-wrap items-center gap-2">
          <ToggleGroup options={VIEW_OPTIONS} value={view} onChange={setView} />
          <EngineGroupFilterDropdown
            groups={FILTER_GROUPS}
            selected={selected}
            expandedGroups={expandedGroups}
            onToggleEngine={toggleEngine}
            onToggleGroup={toggleGroup}
            onToggleExpand={toggleExpand}
            onClear={() => setSelected([])}
            onSelectAll={() => setSelected(ALL_ENGINE_OPTIONS.map(({ tool, engine }) => ({ tool, engine })))}
          />
          <SectionRangePicker from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
          <ToggleGroup options={SCOPE_OPTIONS} value={scope} onChange={setScope} />
          <ToggleGroup options={GRANULARITY_OPTIONS} value={granularity} onChange={setGranularity} />
          <ToggleGroup options={MODE_OPTIONS} value={mode} onChange={setMode} />
          <ToggleGroup options={UNIT_OPTIONS} value={unit} onChange={setUnit}
            disabled={view === "model"} disabledTitle="모델별 보기는 호출 수 기준입니다" />
          <CopyButton rows={exportRows} />
          <ExportButton filename={view === "tool" ? "tool-usage" : "model-usage"} rows={exportRows} />
        </div>
      }
    >
      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          데이터를 불러오지 못했습니다 — {error.message} ({error.code}). 위 날짜 범위가 올바른지 확인해 주세요.
        </div>
      )}
      {!error && (loading && !toolTs ? <Skeleton h={72} variant="chartTable" /> : (
        <div className="animate-fade-in">
          <KpiRow cols={3}>
            <KpiCard label="메시지 수" value={fmt.int(totalRequests * 2)} tone="brand" hero sublabel="이 기간 전체 (사용자+AI)" />
            <KpiCard label="세션 수" value={fmt.int(kpiSessionIds.size)} sublabel="이 기간 고유 대화 수" />
            {/* 3번째 카드만 뷰를 따라간다 — 자리·형식은 같고 내용만 절단면에 맞춤. */}
            {view === "tool" ? (
              <KpiCard label="최다 사용 도구" value={topTool ? toolLabel(topTool[0]) : "—"}
                sublabel={topTool
                  ? `${fmt.int(topTool[1] * 2)} 메시지${unclassifiedCount > 0 ? ` · 미분류 ${fmt.int(unclassifiedCount * 2)} 제외` : ""}`
                  : unclassifiedCount > 0
                    ? `분류된 도구 사용 없음 · 미분류 ${fmt.int(unclassifiedCount * 2)} 메시지`
                    : "이 기간 사용 없음"} />
            ) : (
              <KpiCard label="최다 호출 모델" value={topModel ? prettyModelId(topModel[0]) : "—"}
                sublabel={topModel ? `${fmt.int(topModel[1])}회 호출` : "이 기간 호출 없음"} />
            )}
          </KpiRow>
          {view === "tool" ? (
            <>
              <EngineUsageTrendChart series={series} items={items} unit={unit} />
              <details className="mt-4 group">
                <summary className="flex cursor-pointer items-center gap-1.5 text-[12px] font-medium text-ink-400 hover:text-ink-600 select-none [&::-webkit-details-marker]:hidden">
                  <span className="inline-block transition-transform group-open:rotate-90">▶</span>
                  기간별 상세 수치 보기
                </summary>
                <div className="mt-2">
                  <EngineUsageTrendTable series={series} items={items} cumulative={mode === "cumulative"} unit={unit} onToggleExpand={toggleExpand} />
                </div>
              </details>
            </>
          ) : (
            <ModelUsageView series={modelSeries} range={range} filterActive={!allEnginesSelected} />
          )}
        </div>
      ))}
    </Panel>
  );
}
